// Lote: la unidad de manejo del campo. Sigue en qué etapa está el cultivo con
// los grados día de sus estaciones (promediadas) desde la siembra. El cálculo
// lo hace el backend (backend/src/domain/entities/Lot.ts); acá están los tipos
// y la presentación.

import type { DeviceComponent } from './Component';

export interface LotStage {
  name: string;
  zadoks: string | null;
  // Grados día acumulados en que empieza la etapa.
  gdd: number;
  // Qué conviene vigilar en esta etapa.
  watch: string | null;
}

export interface Reading {
  value: number;
  recordedAt: string;
}

export interface LotStation {
  id: string;
  name: string;
  status: 'online' | 'offline';
  hasAir: boolean;
  hasRain: boolean;
  hasWind: boolean;
  // Lo último de la estación (para el "ahora" del lote).
  now: {
    temperature: Reading | null;
    humidity: Reading | null;
    windSpeed: Reading | null;
    pressure: Reading | null;
    // null = sin pluviómetro.
    rainToday: number | null;
    rainLast7d: number | null;
    // Cada sonda de suelo con su último valor.
    soil: (Reading & { channel: number })[];
  };
}

// Lo último de una estación (también la lista de sensores).
export type StationNow = LotStation['now'];

export interface LotStatus {
  today: string;
  computed: number;
  countedDays: number;
  missingDays: number;
  last7Avg: number | null;
  phenology: {
    // Calculado + corrección del campo.
    accumulated: number;
    stageIndex: number;
    nextIndex: number | null;
    remaining: number | null;
    progressPct: number;
    nextDate: string | null;
  };
  rain: { total: number; perStation: { id: string; name: string; mm: number }[] } | null;
  last7: { coldest: number | null; hottest: number | null; wetHours: number | null; rainMm: number | null };
}

export interface Lot {
  id: string;
  siteId: string | null;
  name: string;
  areaHa: number | null;
  crop: string;
  baseTemp: number;
  stages: LotStage[];
  // Siembra, o último corte/pastoreo (null = sin sembrar).
  cycleStart: string | null;
  gddOffset: number;
  // Etapa de la última revisión del servidor (la que usan las alertas).
  stageIndex: number | null;
  // Avisar (push) cuando pasa a la etapa siguiente.
  notifyStageChange: boolean;
  createdAt: string;
  // Suyo, o compartido para ver o para editar ('control': cultivo, etapas,
  // siembra y corrección; las estaciones, compartir y borrar son del dueño).
  access: LotAccess;
  ownerName: string;
  stations: LotStation[];
  // null = sin fecha de siembra.
  status: LotStatus | null;
}

export type LotAccess = 'owner' | 'control' | 'view';

export const LOT_ACCESS_LABEL: Record<Exclude<LotAccess, 'owner'>, string> = {
  view: 'Solo ver',
  control: 'Ver y editar',
};

export interface LotShare {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  level: 'view' | 'control';
  createdAt: string;
}

// Un lote en el panel de admin: con el email del dueño.
export type AdminLot = Lot & { ownerEmail: string };

// Lo que necesita el formulario del admin: campos y estaciones del dueño.
export interface AdminLotFormOptions {
  sites: { id: string; name: string; lat: number | null; lng: number | null }[];
  stations: { id: string; name: string; components: DeviceComponent[] }[];
  lotOfStation: Record<string, { id: string; name: string }>;
}

export interface CropPreset {
  key: string;
  crop: string;
  baseTemp: number;
  stages: LotStage[];
  // Pasturas: el acumulado se reinicia en cada corte o pastoreo.
  resets: boolean;
}

export interface LotInput {
  name: string;
  siteId: string | null;
  areaHa: number | null;
  crop: string;
  baseTemp: number;
  stages: LotStage[];
  cycleStart: string | null;
  stationIds: string[];
  notifyStageChange: boolean;
}

// Una pastura (o cualquier cultivo cuyo ciclo se reinicia): se ofrece
// "reiniciar desde hoy" en vez de corregir la etapa.
export function isPasture(crop: string): boolean {
  return /pastura|pradera|verdeo|alfalfa/i.test(crop);
}

// "2026-07-13" -> "13/07/2026"
export function formatDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}

export function stageTitle(stage: LotStage): string {
  return stage.zadoks ? `${stage.name} (${stage.zadoks})` : stage.name;
}

export const formatGdd = (n: number) => Math.round(n).toLocaleString('es-AR');

// --- El "ahora" del lote: promedio de sus estaciones ---

// Una lectura de hace más de esto no entra en el promedio: una estación
// caída no puede tirar el valor del lote.
export const LOT_NOW_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface LotNow {
  temperature: number | null;
  humidity: number | null;
  windSpeed: number | null;
  pressure: number | null;
  // Promedio de todas las sondas de suelo del lote.
  soil: number | null;
  // Promedio de los pluviómetros (lluvia de hoy y de la última semana).
  rainToday: number | null;
  rainLast7d: number | null;
  // Cuántas estaciones aportaron temperatura reciente, y de cuándo es el dato más nuevo.
  freshStations: number;
  // Las estaciones que aportaron algún dato reciente (para decir de dónde sale el promedio).
  sources: string[];
  latestAt: string | null;
}

const average = (values: number[]) => (values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : null);

export function lotNow(stations: LotStation[], now = Date.now()): LotNow {
  const fresh = (r: Reading | null): r is Reading => r !== null && now - new Date(r.recordedAt).getTime() <= LOT_NOW_MAX_AGE_MS;
  const values = (pick: (s: LotStation) => Reading | null) => stations.map(pick).filter(fresh);
  const temps = values((s) => s.now.temperature);
  const rain = stations.filter((s) => s.now.rainToday !== null);
  const readingsOf = (s: LotStation): Reading[] =>
    [s.now.temperature, s.now.humidity, s.now.windSpeed, s.now.pressure, ...(s.now.soil ?? [])].filter((r): r is Reading => r !== null);
  const all = stations.flatMap(readingsOf);
  return {
    temperature: average(temps.map((r) => r.value)),
    humidity: average(values((s) => s.now.humidity).map((r) => r.value)),
    windSpeed: average(values((s) => s.now.windSpeed).map((r) => r.value)),
    pressure: average(values((s) => s.now.pressure).map((r) => r.value)),
    soil: average(stations.flatMap((s) => s.now.soil ?? []).filter(fresh).map((r) => r.value)),
    rainToday: average(rain.map((s) => s.now.rainToday as number)),
    rainLast7d: average(rain.map((s) => s.now.rainLast7d ?? 0)),
    freshStations: temps.length,
    sources: stations.filter((s) => readingsOf(s).some(fresh)).map((s) => s.name),
    latestAt: all.map((r) => r.recordedAt).sort().pop() ?? null,
  };
}

// --- Del "ahora" del lote al detalle de una estación ---

export type LotNowMetric = 'temperatura' | 'humedad' | 'viento' | 'lluvia' | 'presion';

const round1Lot = (n: number) => Math.round(n * 10) / 10;

// Las estaciones del lote que miden esa métrica (con su último valor), para
// abrir el detalle de la métrica en una de ellas: si hay una sola, directo.
export function stationsForMetric(stations: LotStation[], metric: LotNowMetric): { id: string; label: string }[] {
  const pick = (s: LotStation): string | null => {
    switch (metric) {
      case 'temperatura':
        return s.hasAir ? (s.now.temperature ? `${round1Lot(s.now.temperature.value)}°` : '—') : null;
      case 'humedad':
        return s.hasAir ? (s.now.humidity ? `${Math.round(s.now.humidity.value)}%` : '—') : null;
      case 'viento':
        return s.hasWind ? (s.now.windSpeed ? `${Math.round(s.now.windSpeed.value)} km/h` : '—') : null;
      case 'lluvia':
        return s.hasRain ? `${round1Lot(s.now.rainToday ?? 0)} mm hoy` : null;
      case 'presion':
        return s.now.pressure ? `${Math.round(s.now.pressure.value)} hPa` : null;
    }
  };
  return stations.flatMap((s) => {
    const value = pick(s);
    return value === null ? [] : [{ id: s.id, label: `${s.name} · ${value}` }];
  });
}

// Las sondas de suelo del lote, para abrir el detalle de una.
export function soilProbesOf(stations: LotStation[]): { id: string; channel: number; label: string }[] {
  return stations.flatMap((s) =>
    (s.now.soil ?? []).map((p) => ({
      id: s.id,
      channel: p.channel,
      label: `${s.name}${(s.now.soil ?? []).length > 1 ? ` · sonda ${p.channel}` : ''} · ${Math.round(p.value)}%`,
    }))
  );
}
